import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Megaphone, TrendingUp, Users, Download, Sparkles, Inbox, Send, Heart, Ticket, FileText, Plus, Mail, RefreshCw, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

/**
 * Cold-start marketing dashboard for the founding team.
 * - Funnel metrics (scouted → invited → claimed).
 * - Bulk CSV import for social-blitz lead lists.
 * - MCLA scrape + USILA scrape buttons (HeadhunterAgent shortcuts).
 */
export default function MarketingDashboard() {
  return <Navigate to="/hq" replace />;
}

export function DataVaultSection() {
  const [snaps, setSnaps] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/backup/list").then(({ data }) => setSnaps(data || [])).catch(() => {});
  };
  useEffect(load, []);

  const runBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      toast.success(`Snapshot created — ${data.total_docs.toLocaleString()} docs (${(data.size_bytes / 1048576).toFixed(1)} MB)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backup failed");
    } finally { setBusy(false); }
  };

  const download = async (file) => {
    try {
      const { data } = await api.get(`/admin/backup/download/${file}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="data-vault-section">
      <div className="text-xs tracking-widest text-volt">DATA VAULT · TKG BACKUP</div>
      <p className="text-xs text-neutral-500 mt-1">
        Full snapshot of every collection (coach profiles, jobs, comp records, leads, users...) as gzipped JSON. Auto-runs Sundays 7 AM ET; last 5 kept. Download and store off-site — this data is the moat.
      </p>
      <div className="mt-4">
        <Btn data-testid="run-backup-btn" onClick={runBackup} busy={busy}>
          Run Backup Now
        </Btn>
      </div>
      {snaps.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="backup-snapshot-list">
          {snaps.map((s) => (
            <div key={s.file} className="flex flex-wrap items-center justify-between gap-2 border border-neutral-800 px-3 py-2">
              <div className="min-w-0">
                <div className="text-xs text-neutral-200 truncate">{s.file}</div>
                <div className="text-[10px] text-neutral-500">
                  {s.at ? new Date(s.at).toLocaleString() : ""}{s.total_docs ? ` · ${s.total_docs.toLocaleString()} docs` : ""}{s.size_bytes ? ` · ${(s.size_bytes / 1048576).toFixed(1)} MB` : ""}
                </div>
              </div>
              <button
                data-testid={`download-backup-${s.file}`}
                onClick={() => download(s.file)}
                className="inline-flex items-center gap-1.5 text-xs tracking-widest text-volt border border-volt/50 px-3 py-1.5 hover:bg-volt hover:text-white transition-colors"
              >
                <Download size={12} /> DOWNLOAD
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CoachingTreeAdminSection() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = () => {
    api.get("/admin/tkg/tree-status").then(({ data }) => setStatus(data)).catch(() => {});
  };
  useEffect(loadStatus, []);

  const rebuild = async () => {
    setBusy(true);
    try {
      await api.post("/admin/tkg/rebuild-tree");
      toast.success("Coaching tree rebuild started — runs in background (~1-2 min)");
      setTimeout(() => { loadStatus(); setBusy(false); }, 90000);
      setTimeout(loadStatus, 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rebuild failed to start");
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="coaching-tree-admin-section">
      <div className="text-xs tracking-widest text-volt">COACHING TREE · TKG MAINTENANCE</div>
      <p className="text-xs text-neutral-500 mt-1">
        Rebuilds mentor/disciple relationships from stint overlaps. Idempotent — preserves manual mentors. Run after big imports or dedupes.
      </p>
      {status && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card icon={Users} label="Profiles w/ mentors" value={status.profiles_with_inferred_mentors} accent="volt" />
          <Card icon={Users} label="Profiles w/ disciples" value={status.profiles_with_disciples} />
          <Card icon={Clock} label="Last rebuilt" value={status.last_rebuilt_at ? new Date(status.last_rebuilt_at).toLocaleDateString() : "—"} />
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <Btn data-testid="rebuild-coaching-tree-btn" onClick={rebuild} busy={busy}>
          Rebuild Coaching Trees
        </Btn>
        <button data-testid="refresh-tree-status-btn" onClick={loadStatus} className="text-xs tracking-widest text-neutral-400 hover:text-volt transition-colors inline-flex items-center gap-1">
          <RefreshCw size={12} /> REFRESH STATUS
        </button>
      </div>
    </div>
  );
}

export function CoachActivityTrendSection({ readOnly = false }) {
  const [trend, setTrend] = useState(null);
  const [nudgeStats, setNudgeStats] = useState(null);
  const [waveFunnel, setWaveFunnel] = useState(null);
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [hotBusy, setHotBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/coach-activity/trend").then(({ data }) => setTrend(data)).catch(() => {});
    api.get("/admin/profile-nudge/stats").then(({ data }) => setNudgeStats(data)).catch(() => {});
    api.get("/admin/claim-funnel/tracker-wave").then(({ data }) => setWaveFunnel(data)).catch(() => {});
  }, []);

  const runNudge = async () => {
    setNudgeBusy(true);
    try {
      const { data } = await api.post("/admin/profile-nudge/run");
      toast.success(`Nudges — sent ${data.sent}, skipped ${data.skipped}, failed ${data.failed}`);
      api.get("/admin/profile-nudge/stats").then(({ data: d }) => setNudgeStats(d)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge sweep failed");
    } finally { setNudgeBusy(false); }
  };

  const sendHotList = async () => {
    setHotBusy(true);
    try {
      const { data } = await api.post("/admin/hot-list/send-sample");
      toast.success(`Hot list sample sent to ${data.sent_to} admin(s) — ${data.coaches} coaches${data.sample_data ? " (sample data)" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hot list send failed");
    } finally { setHotBusy(false); }
  };

  const maxSaves = trend ? Math.max(1, ...trend.days.map((d) => d.saves)) : 1;

  return (
    <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="coach-activity-trend-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs tracking-widest text-volt flex items-center gap-2"><Clock size={13} /> COACH PROFILE ACTIVITY — 7 DAYS</div>
        {!readOnly && (
        <div className="flex items-center gap-2">
          <button onClick={sendHotList} disabled={hotBusy} data-testid="send-hot-list-btn"
            className="px-3 min-h-[30px] text-[10px] tracking-widest border border-volt text-volt hover:bg-volt hover:text-white transition-colors disabled:opacity-50">
            {hotBusy ? "SENDING…" : "EMAIL HOT LIST SAMPLE"}
          </button>
          <button onClick={runNudge} disabled={nudgeBusy} data-testid="run-profile-nudge-btn"
            className="px-3 min-h-[30px] text-[10px] tracking-widest border border-blaze text-blaze hover:bg-blaze hover:text-white transition-colors disabled:opacity-50">
            {nudgeBusy ? "SENDING…" : "NUDGE STALE PROFILES NOW"}
          </button>
        </div>
        )}
      </div>
      <p className="text-[11px] text-neutral-500 mt-1">Profile saves per day. Nudges auto-run daily at 17:00 UTC — coaches with unfinished profiles, quiet 3-30 days, max once / 14 days each.</p>
      {nudgeStats && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="nudge-stats-chips">
          <span className="px-2.5 py-1 border border-neutral-700 text-[10px] tracking-widest text-neutral-300">NUDGED 28D: <b className="text-white">{nudgeStats.nudged}</b></span>
          <span className="px-2.5 py-1 border border-neutral-700 text-[10px] tracking-widest text-neutral-300">CAME BACK ≤7D: <b style={{ color: "#FF3B30" }}>{nudgeStats.returned}</b>{nudgeStats.nudged > 0 && <span className="text-neutral-500"> ({Math.round((nudgeStats.returned / nudgeStats.nudged) * 100)}%)</span>}</span>
          <span className="px-2.5 py-1 border border-neutral-700 text-[10px] tracking-widest text-neutral-300">FINISHED PROFILE: <b style={{ color: "#FF3B30" }}>{nudgeStats.completed}</b></span>
        </div>
      )}
      {waveFunnel && waveFunnel.profiles > 0 && (
        <div className="mt-4" data-testid="tracker-wave-funnel">
          <div className="text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800 pb-1.5">TRACKER WAVE FUNNEL · COACHTRACKER INGESTION</div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {[["PROFILES", waveFunnel.profiles], ["EMAILS FOUND", waveFunnel.with_email], ["PITCHED", waveFunnel.pitched], ["SENT", waveFunnel.sent], ["CLAIM LINK CLICKED", waveFunnel.clicked], ["ACCOUNTS CREATED", waveFunnel.claimed]].map(([label, n], i, arr) => (
              <span key={label} className="flex items-center gap-1.5">
                <span className="px-2.5 py-1.5 border border-neutral-700 text-[10px] tracking-widest text-neutral-300">{label}: <b className={i >= 4 ? "" : "text-white"} style={i >= 4 ? { color: "#FF3B30" } : {}}>{n}</b></span>
                {i < arr.length - 1 && <span className="text-neutral-600 text-xs">→</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      {!trend ? (
        <div className="mt-4 text-neutral-500 text-sm">Loading…</div>
      ) : (
        <>
          <div className="mt-5 flex items-end gap-2 h-28" data-testid="activity-trend-bars">
            {trend.days.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1" data-testid={`trend-day-${d.date}`}>
                <span className="text-[10px] text-neutral-300">{d.saves || ""}</span>
                <div className="w-full" style={{ backgroundColor: "#FF3B30", height: `${Math.round((d.saves / maxSaves) * 72)}px`, minHeight: d.saves ? "4px" : "1px", opacity: d.saves ? 1 : 0.15 }} />
                <span className="text-[9px] text-neutral-500">{d.date.slice(5).replace("-", "/")}</span>
                <span className="text-[9px] text-neutral-600">{d.coaches ? `${d.coaches}c` : ""}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800 pb-1.5">TOP EDITORS — LAST 7 DAYS</div>
          {trend.top.length === 0 ? (
            <div className="mt-3 text-neutral-500 text-sm" data-testid="trend-top-empty">No coaches edited their profile this week.</div>
          ) : (
            <div className="mt-2 space-y-1.5" data-testid="trend-top-list">
              {trend.top.map((c, i) => (
                <div key={c.coach_profile_id} className="flex items-center gap-3 border-b border-neutral-900 pb-1.5" data-testid={`trend-top-${i}`}>
                  <span className="text-[10px] text-neutral-600 w-5">{i + 1}.</span>
                  <a href={`/coaches/${c.coach_profile_id}`} className="text-xs text-white hover:text-volt underline underline-offset-2 min-w-0 truncate">{c.name || "Unknown coach"}</a>
                  <span className="text-[10px] text-blaze font-bold ml-auto whitespace-nowrap">{c.saves} save{c.saves === 1 ? "" : "s"}</span>
                  <span className="text-[10px] text-neutral-600 whitespace-nowrap">{(c.last_at || "").slice(5, 16).replace("T", " ")}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ActivityFeedSection({ readOnly = false }) {
  const [events, setEvents] = useState(null);
  const [hours, setHours] = useState(48);
  const [pulseBusy, setPulseBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/activity?hours=${hours}`).then(({ data }) => setEvents(data.events)).catch(() => {});
  }, [hours]);

  const sendPulse = async () => {
    setPulseBusy(true);
    try {
      const { data } = await api.post("/admin/daily-pulse/run");
      toast.success(`Daily Pulse sent to ${data.sent_to} admin(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pulse send failed");
    } finally { setPulseBusy(false); }
  };

  return (
    <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="activity-feed-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs tracking-widest text-volt flex items-center gap-2"><Clock size={13} /> TEAM ACTIVITY FEED</div>
        <div className="flex items-center gap-2">
          {[24, 48, 168].map((h) => (
            <button key={h} onClick={() => setHours(h)} data-testid={`activity-range-${h}`}
              className={`px-2 min-h-[30px] text-[10px] tracking-widest border ${hours === h ? "border-blaze text-blaze" : "border-neutral-700 text-neutral-400 hover:border-blaze"} transition-colors`}>
              {h === 168 ? "7D" : `${h}H`}
            </button>
          ))}
          <button onClick={sendPulse} disabled={pulseBusy} data-testid="send-daily-pulse-btn"
            className={`px-3 min-h-[30px] text-[10px] tracking-widest border border-volt text-volt hover:bg-volt hover:text-white transition-colors disabled:opacity-50 ${readOnly ? "hidden" : ""}`}>
            {pulseBusy ? "SENDING…" : "EMAIL PULSE NOW"}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-neutral-500 mt-1">Who did what — approvals, resets, drip sends, claims. Also emailed daily at 11:00 UTC.</p>
      {!events ? (
        <div className="mt-4 text-neutral-500 text-sm">Loading…</div>
      ) : events.length === 0 ? (
        <div className="mt-4 text-neutral-500 text-sm" data-testid="activity-empty">No activity in this window.</div>
      ) : (
        <div className="mt-4 space-y-1.5 max-h-[360px] overflow-y-auto" data-testid="activity-list">
          {events.map((e, i) => (
            <div key={i} className="flex items-start gap-3 border-b border-neutral-900 pb-1.5" data-testid={`activity-event-${i}`}>
              <span className="text-[10px] text-neutral-600 whitespace-nowrap mt-0.5 w-24 shrink-0">{(e.at || "").slice(5, 16).replace("T", " ")}</span>
              <span className="text-xs text-neutral-300 min-w-0"><b className="text-volt">{e.actor}</b> — {e.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TestimonialManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ quote: "", author_name: "", author_title: "", program: "" });
  const load = () => api.get("/admin/testimonials").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.quote.trim() || !form.author_name.trim()) return toast.error("Quote and name are required");
    await api.post("/admin/testimonials", form);
    setForm({ quote: "", author_name: "", author_title: "", program: "" });
    toast.success("Testimonial added — approve it to show on the landing page");
    load();
  };
  const toggle = async (t) => {
    await api.patch(`/admin/testimonials/${t.id}`, { ...t, approved: !t.approved });
    load();
  };
  const remove = async (t) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${t.id}`);
    load();
  };
  return (
    <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="testimonial-manager">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs tracking-widest text-volt">TESTIMONIAL WALL · {items.filter((t) => t.approved).length} LIVE ON LANDING PAGE</div>
        <button data-testid="testimonial-add-toggle" onClick={() => setOpen(!open)} className="min-h-[40px] px-4 border border-volt text-volt text-xs tracking-widest hover:bg-volt hover:text-white transition-colors">
          {open ? "CLOSE" : "ADD TESTIMONIAL"}
        </button>
      </div>
      {open && (
        <div className="mt-4 grid gap-2">
          <textarea data-testid="testimonial-quote-input" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={3}
            placeholder="Paste the AD's quote (from a reply to your testimonial-request email)…"
            className="bg-black border border-neutral-700 p-3 text-sm text-neutral-200 focus:border-volt outline-none" />
          <div className="grid md:grid-cols-3 gap-2">
            <input data-testid="testimonial-name-input" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} placeholder="Name" className="bg-black border border-neutral-700 p-3 text-sm focus:border-volt outline-none" />
            <input value={form.author_title} onChange={(e) => setForm({ ...form, author_title: e.target.value })} placeholder="Title (e.g. Athletic Director)" className="bg-black border border-neutral-700 p-3 text-sm focus:border-volt outline-none" />
            <input value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} placeholder="Program / School" className="bg-black border border-neutral-700 p-3 text-sm focus:border-volt outline-none" />
          </div>
          <button data-testid="testimonial-save-btn" onClick={save} className="min-h-[44px] bg-volt text-white heading text-sm tracking-widest hover:opacity-90">SAVE</button>
        </div>
      )}
      <div className="mt-4 space-y-2">
        {items.length === 0 && <div className="text-neutral-500 text-xs">No testimonials yet — replies to your "job filled" emails are the best source.</div>}
        {items.map((t) => (
          <div key={t.id} className="border border-neutral-800 p-3 flex items-start gap-3" data-testid={`testimonial-row-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-neutral-300 line-clamp-2">“{t.quote}”</div>
              <div className="text-[10px] tracking-widest text-neutral-500 mt-1">{t.author_name} · {[t.author_title, t.program].filter(Boolean).join(" · ")}</div>
            </div>
            <button data-testid={`testimonial-approve-${t.id}`} onClick={() => toggle(t)}
              className={`min-h-[36px] px-3 text-[10px] tracking-widest shrink-0 ${t.approved ? "bg-volt text-white" : "border border-neutral-600 text-neutral-400 hover:border-volt hover:text-volt"}`}>
              {t.approved ? "LIVE" : "APPROVE"}
            </button>
            <button data-testid={`testimonial-delete-${t.id}`} onClick={() => remove(t)} className="min-h-[36px] px-2 text-neutral-600 hover:text-red-500 text-xs">✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Pill({ label, value, accent }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-neutral-500">{label}</span>
      <span className={`heading ${accent === "blaze" ? "text-blaze" : accent === "volt" ? "text-volt" : "text-white"}`}>{value ?? 0}</span>
    </div>
  );
}

export function Card({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className={`border ${accent === "blaze" ? "border-blaze" : accent === "volt" ? "border-volt" : "border-neutral-800"} bg-appcard p-4`}>
      <div className="flex items-center gap-1.5 text-[10px] tracking-widest text-neutral-500"><Icon size={12} /> {label.toUpperCase()}</div>
      <div className={`heading text-3xl mt-1 ${accent === "blaze" ? "text-blaze" : accent === "volt" ? "text-volt" : ""}`}>{value}</div>
      {sub && <div className="text-[9px] tracking-widest text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

export function Btn({ children, busy, ...rest }) {
  return (
    <button {...rest} disabled={busy} className="min-h-[48px] border border-neutral-700 hover:border-volt hover:text-volt text-sm tracking-widest disabled:opacity-50 px-4 transition-colors">
      {busy ? "RUNNING…" : children}
    </button>
  );
}

export function PitchEditor({ lead, onSaved, onSent }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(lead.pitch_text || "");
  const [subject, setSubject] = useState(lead.pitch_subject || `${lead.program_name || ""} — re: your ${lead.title || ""} opening`);
  const [recipient, setRecipient] = useState(lead.contact_email || lead.pitch_recipient || "");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/outreach/leads/${lead.id}/pitch`, { pitch_text: text, subject });
      onSaved?.({ pitch_text: text, pitch_subject: subject, pitch_edited_at: new Date().toISOString() });
      setEditing(false);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const send = async () => {
    if (!recipient) return toast.warning("Enter the recipient email first");
    if (!text.trim()) return toast.warning("Pitch is empty");
    setSending(true);
    try {
      const { data } = await api.post(`/outreach/leads/${lead.id}/send-pitch`, {
        to: recipient, subject, pitch_text: text,
      });
      toast.success(`Sent · Resend id ${data.resend_id?.slice(0, 8) || "ok"}`);
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed — check the email log");
    } finally { setSending(false); }
  };

  return (
    <div className="mt-3 bg-[#070707] border border-neutral-800 p-3" data-testid={`pitch-editor-${lead.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={!editing}
          data-testid={`pitch-subject-${lead.id}`}
          placeholder="Subject"
          className="flex-1 bg-appcard border border-neutral-800 px-2 py-1.5 text-xs text-neutral-200 disabled:opacity-70"
        />
        {editing ? (
          <button onClick={save} disabled={saving} data-testid={`save-pitch-${lead.id}`} className="text-[10px] tracking-widest px-3 py-1.5 border border-volt text-volt hover:bg-volt hover:text-white disabled:opacity-50">
            {saving ? "SAVING…" : "SAVE"}
          </button>
        ) : (
          <button onClick={() => setEditing(true)} data-testid={`edit-pitch-${lead.id}`} className="text-[10px] tracking-widest px-3 py-1.5 border border-neutral-700 hover:border-volt hover:text-volt">
            EDIT
          </button>
        )}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={!editing}
        data-testid={`pitch-text-${lead.id}`}
        rows={8}
        className="w-full bg-appcard border border-neutral-800 px-2 py-2 text-[11px] text-neutral-300 font-mono whitespace-pre-wrap disabled:opacity-80"
      />
      <div className="flex items-center gap-2 mt-2">
        <input
          type="email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          data-testid={`pitch-recipient-${lead.id}`}
          placeholder="recipient@school.edu"
          className="flex-1 bg-appcard border border-neutral-800 px-2 py-1.5 text-xs text-neutral-200"
        />
        <button onClick={send} disabled={sending} data-testid={`send-pitch-${lead.id}`} className="min-h-[36px] px-3 bg-blaze text-white text-[10px] tracking-widest hover:glow-blaze transition-all disabled:opacity-50 flex items-center gap-1.5">
          <Send size={12} /> {sending ? "SENDING…" : "SEND VIA RESEND"}
        </button>
      </div>
      {lead.last_resend_id && <div className="mt-2 text-[10px] text-volt">✓ Last send · Resend {lead.last_resend_id.slice(0, 12)}…</div>}
    </div>
  );
}


// Delivery Log — last 50 Resend sends with live delivered/bounced status.
export function DeliveryLogSection() {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/email-log")
      .then(({ data }) => setLog(data))
      .catch(() => toast.error("Failed to load delivery log"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="mt-8 border border-neutral-800 bg-appcard p-5" data-testid="delivery-log-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs tracking-widest text-volt flex items-center gap-2"><Mail size={14} /> DELIVERY LOG · LAST 50 RESEND SENDS</div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="delivery-log-refresh"
          className="min-h-[36px] px-3 border border-neutral-700 text-neutral-300 text-[10px] tracking-widest hover:border-volt hover:text-volt transition-colors disabled:opacity-50 flex items-center gap-1.5"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> {loading ? "CHECKING…" : "REFRESH STATUS"}
        </button>
      </div>
      <p className="text-[11px] text-neutral-500 mt-2">
        Every email the platform hands to Resend, with its live delivery result. <span className="text-red-400">FAILED</span> = Resend rejected the send (email never left). <span className="text-red-400">BOUNCED</span> = recipient server refused it. Fix bad addresses before retrying.
      </p>

      {log && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="delivery-log-counts">
          <Card icon={CheckCircle2} label="Delivered" value={log.counts.delivered} accent="volt" />
          <Card icon={Clock} label="In transit" value={log.counts.in_transit} />
          <Card icon={AlertTriangle} label="Bounced" value={log.counts.bounced} accent={log.counts.bounced > 0 ? "blaze" : undefined} />
          <Card icon={AlertTriangle} label="Failed" value={log.counts.failed} accent={log.counts.failed > 0 ? "blaze" : undefined} />
        </div>
      )}

      <div className="mt-4 overflow-x-auto">
        {!log ? (
          <div className="text-neutral-500 text-sm py-4">Loading…</div>
        ) : log.entries.length === 0 ? (
          <div className="text-neutral-500 text-sm py-4" data-testid="delivery-log-empty">No emails sent from this environment yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="delivery-log-table">
            <thead>
              <tr className="text-left text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800">
                <th className="py-2 pr-3">STATUS</th>
                <th className="py-2 pr-3">TO</th>
                <th className="py-2 pr-3">SUBJECT</th>
                <th className="py-2 pr-3">FROM</th>
                <th className="py-2">SENT</th>
              </tr>
            </thead>
            <tbody>
              {log.entries.map((e, i) => (
                <tr key={e.id || i} className="border-b border-neutral-900 align-top" data-testid={`delivery-row-${i}`}>
                  <td className="py-2 pr-3"><DeliveryBadge entry={e} /></td>
                  <td className="py-2 pr-3 text-neutral-200 text-xs whitespace-nowrap">{e.to}</td>
                  <td className="py-2 pr-3 text-neutral-400 text-xs max-w-[280px] truncate">{e.subject}</td>
                  <td className="py-2 pr-3 text-neutral-500 text-xs whitespace-nowrap">{e.from}</td>
                  <td className="py-2 text-neutral-500 text-xs whitespace-nowrap">{e.sent_at ? new Date(e.sent_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <BouncePanel />
    </div>
  );
}

function BouncePanel() {
  const [data, setData] = useState(null);
  const [fixing, setFixing] = useState(false);

  const load = () => api.get("/admin/bounces").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runFix = async () => {
    setFixing(true);
    try {
      const { data: d } = await api.post("/admin/bounces/fix");
      if (d.started === 0) { toast.info("No unfixed bounces to work on"); setFixing(false); return; }
      toast.success(`Claude is working on ${d.started} bounced address${d.started > 1 ? "es" : ""} — results appear below in ~1 min`);
      setTimeout(() => { load(); setFixing(false); }, 45000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fix run failed");
      setFixing(false);
    }
  };

  if (!data || data.bounces.length === 0) return null;
  return (
    <div className="mt-6 border-t border-neutral-800 pt-4" data-testid="bounce-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs tracking-widest text-blaze flex items-center gap-2">
          <AlertTriangle size={13} /> BOUNCED ADDRESSES · {data.bounces.length}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="bounce-refresh" className="min-h-[34px] px-3 border border-neutral-700 text-neutral-400 text-[10px] tracking-widest hover:border-volt hover:text-volt transition-colors">REFRESH</button>
          <button onClick={runFix} disabled={fixing || data.unfixed === 0} data-testid="bounce-fix-btn"
            className="min-h-[34px] px-3 border border-volt text-volt text-[10px] tracking-widest hover:bg-volt hover:text-white transition-colors disabled:opacity-50 flex items-center gap-1.5">
            {fixing ? <RefreshCw size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            {fixing ? "CLAUDE FIXING…" : `FIX WITH CLAUDE (${data.unfixed})`}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-neutral-500 mt-1">
        These addresses are blocked from all outreach. Claude re-reads each lead&apos;s source page to find the corrected email — confident fixes re-queue the draft automatically (addressed &ldquo;Coach,&rdquo;). Runs daily on its own too.
      </p>
      <div className="mt-3 space-y-1">
        {data.bounces.map((b) => (
          <div key={b.email} className="border border-neutral-900 px-3 py-2 text-xs flex items-center gap-2 flex-wrap" data-testid={`bounce-row-${b.email.replace(/[@.]/g, "-")}`}>
            <span className="text-neutral-200 font-mono">{b.email}</span>
            <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-red-600 text-red-500">{(b.event || "bounced").toUpperCase()}</span>
            {b.fix_status === "fixed" ? (
              <span className="text-[10px] text-volt">✓ FIXED → <span className="font-mono">{b.corrected_email}</span> · draft re-queued</span>
            ) : b.fix_status === "no_fix_found" ? (
              <span className="text-[10px] text-neutral-500" title={b.fix_reason}>NO FIX FOUND — needs manual correction</span>
            ) : b.fix_status === "skipped_test" ? (
              <span className="text-[10px] text-neutral-600">TEST ADDRESS — skipped</span>
            ) : (
              <span className="text-[10px] text-amber-400">AWAITING CLAUDE FIX</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DeliveryBadge({ entry }) {
  const s = entry.delivery_status || (entry.error ? "failed" : "sent");
  const cfg = {
    delivered: { label: "DELIVERED", cls: "border-volt/60 text-volt" },
    opened: { label: "OPENED", cls: "border-volt/60 text-volt" },
    clicked: { label: "CLICKED", cls: "border-volt/60 text-volt" },
    bounced: { label: "BOUNCED", cls: "border-red-600 text-red-500" },
    complained: { label: "SPAM COMPLAINT", cls: "border-red-600 text-red-500" },
    failed: { label: "FAILED", cls: "border-red-600 text-red-500" },
  }[s] || { label: "IN TRANSIT", cls: "border-neutral-600 text-neutral-400" };
  return (
    <span className="inline-block">
      <span className={`text-[9px] tracking-widest px-1.5 py-0.5 border whitespace-nowrap ${cfg.cls}`}>{cfg.label}</span>
      {entry.error && <div className="text-[10px] text-red-400/80 mt-1 max-w-[200px]">{String(entry.error).slice(0, 120)}</div>}
    </span>
  );
}


// =====================================================================
// Founding Partners section — direct comp, comp codes, feedback queue,
// Operating Manual PDF download. Platform-admin-only (route is gated).
// =====================================================================
// Email Coverage — which conferences/staffs are pitch-ready. HCs get the
// AD-style program pitch with a personal profile-claim link in the footer.
export function EmailCoverageSection() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [readyOnly, setReadyOnly] = useState(false);
  const [draftingId, setDraftingId] = useState(null);
  const [openPitch, setOpenPitch] = useState(null); // {coachId, subject, body, email, claim_url}

  useEffect(() => {
    api.get("/admin/email-coverage").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const draftPitch = async (hc, force = false) => {
    setDraftingId(hc.id);
    try {
      const { data: d } = await api.post(`/admin/coaches/${hc.id}/draft-hc-pitch${force ? "?force=true" : ""}`);
      setOpenPitch({ coachId: hc.id, ...d });
      setData((prev) => !prev ? prev : { ...prev, orgs: prev.orgs.map((o) => ({ ...o, head_coaches: o.head_coaches.map((h) => h.id === hc.id ? { ...h, pitch_drafted: true } : h) })) });
      toast.success(force ? "Pitch re-drafted" : "AD-style HC pitch ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
    } finally { setDraftingId(null); }
  };

  if (!data) return null;
  const q = query.trim().toLowerCase();
  const rows = data.orgs
    .filter((o) => !readyOnly || o.pitch_ready)
    .filter((o) => !q || o.organization.toLowerCase().includes(q) || (o.conference || "").toLowerCase().includes(q))
    .slice(0, 120);

  return (
    <div className="mt-8 border border-neutral-800 bg-appcard p-5" data-testid="email-coverage-section">
      <div className="text-xs tracking-widest text-volt flex items-center gap-2"><Mail size={14} /> EMAIL COVERAGE · PITCH-READY STAFFS</div>
      <p className="text-[11px] text-neutral-500 mt-2 max-w-3xl">
        Head Coaches receive the <span className="text-amber-400">AD-style program pitch</span> (hiring-manager angle), never the coach pitch —
        with a personal &ldquo;review + claim your own profile&rdquo; link in the P.S. Coach-claim sends run through the <a href="/ad/marketing/emails" className="text-volt hover:underline">Email Approval Queue</a> — seeded drafts need your approval, the rest drip at 50/day once you start the campaign.
      </p>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card icon={Users} label="Programs" value={data.summary.orgs} />
        <Card icon={Mail} label="With ≥1 email" value={data.summary.orgs_with_email} />
        <Card icon={Send} label="Pitch-ready (HC email)" value={data.summary.pitch_ready} accent="volt" />
        <Card icon={Users} label="HCs w/ email" value={data.summary.hc_with_email} />
        <Card icon={TrendingUp} label="Total emails" value={data.summary.total_emails} accent="blaze" />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          data-testid="coverage-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search program or conference…"
          className="bg-black border border-neutral-700 px-3 py-2 text-sm w-64 focus:border-volt outline-none"
        />
        <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
          <input data-testid="coverage-ready-only" type="checkbox" checked={readyOnly} onChange={(e) => setReadyOnly(e.target.checked)} />
          Pitch-ready only
        </label>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800">
              <th className="py-2 pr-3">PROGRAM</th>
              <th className="py-2 pr-3">DIV</th>
              <th className="py-2 pr-3">CONF</th>
              <th className="py-2 pr-3">STAFF</th>
              <th className="py-2 pr-3">EMAILS</th>
              <th className="py-2 pr-3">HEAD COACH(ES)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.organization} data-testid={`coverage-row-${o.organization.replace(/\W+/g, "-").toLowerCase()}`} className="border-b border-neutral-900 align-top">
                <td className="py-2 pr-3 text-white">
                  {o.organization}
                  {o.pitch_ready && <span className="ml-2 text-[9px] tracking-widest text-volt border border-volt/40 px-1">READY</span>}
                </td>
                <td className="py-2 pr-3 text-neutral-400">{o.division || "—"}</td>
                <td className="py-2 pr-3 text-neutral-500 text-xs">{o.conference || "—"}</td>
                <td className="py-2 pr-3 text-neutral-300">{o.staff}</td>
                <td className={`py-2 pr-3 ${o.emails ? "text-volt" : "text-neutral-600"}`}>{o.emails}</td>
                <td className="py-2">
                  {o.head_coaches.length === 0 && <span className="text-neutral-600 text-xs">—</span>}
                  {o.head_coaches.map((hc) => (
                    <div key={hc.id} className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-neutral-200 text-xs">{hc.name}</span>
                      {hc.gender && <span className="text-[9px] text-neutral-500">{hc.gender}</span>}
                      {hc.email ? (
                        <button
                          data-testid={`draft-hc-pitch-btn-${hc.id}`}
                          disabled={draftingId === hc.id}
                          onClick={() => draftPitch(hc)}
                          className={`text-[10px] tracking-widest px-2 py-1 border transition-colors ${hc.pitch_drafted ? "border-amber-500/50 text-amber-400 hover:bg-amber-500/10" : "border-volt text-volt hover:bg-volt hover:text-black"}`}
                        >
                          {draftingId === hc.id ? "DRAFTING…" : hc.pitch_drafted ? "VIEW DRAFT" : "DRAFT AD-STYLE PITCH"}
                        </button>
                      ) : (
                        <span className="text-[9px] text-neutral-600 border border-neutral-800 px-1">no email</span>
                      )}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="text-neutral-600 text-sm py-6 text-center">No programs match.</div>}
      </div>

      {openPitch && (
        <div className="mt-5 border border-amber-500/40 bg-black/40 p-4" data-testid="hc-pitch-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[10px] tracking-widest text-amber-400">HC PITCH DRAFT → {openPitch.email}</div>
            <div className="flex gap-2">
              <button
                data-testid="hc-pitch-copy-btn"
                onClick={() => { navigator.clipboard.writeText(`Subject: ${openPitch.subject}\n\n${openPitch.body}`); toast.success("Pitch copied"); }}
                className="text-[10px] tracking-widest border border-volt text-volt px-2 py-1 hover:bg-volt hover:text-black"
              >COPY</button>
              <button
                data-testid="hc-pitch-redraft-btn"
                disabled={draftingId === openPitch.coachId}
                onClick={() => draftPitch({ id: openPitch.coachId }, true)}
                className="text-[10px] tracking-widest border border-neutral-600 text-neutral-400 px-2 py-1 hover:border-volt hover:text-volt"
              >RE-DRAFT</button>
              <button onClick={() => setOpenPitch(null)} className="text-[10px] tracking-widest border border-neutral-700 text-neutral-500 px-2 py-1">CLOSE</button>
            </div>
          </div>
          <div className="mt-2 text-xs text-neutral-400">Subject: <span className="text-neutral-200">{openPitch.subject}</span></div>
          <pre data-testid="hc-pitch-body" className="mt-2 whitespace-pre-wrap text-sm text-neutral-200 font-sans">{openPitch.body}</pre>
          <div className="mt-2 text-[10px] text-neutral-500">Personal claim link embedded in P.S.: <span className="text-neutral-400">{openPitch.claim_url}</span></div>
        </div>
      )}
    </div>
  );
}

export function TeamSection() {
  const [team, setTeam] = useState(null);
  const [busy, setBusy] = useState(null);

  const reload = () => {
    api.get("/admin/team").then(({ data }) => setTeam(data.team)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  const resetPw = async (email) => {
    const pw = window.prompt(`New password for ${email} (min 8 chars):`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(email);
    try {
      const { data } = await api.post("/admin/users/reset-password", { email, new_password: pw });
      toast.success(data.detail);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Reset failed");
    } finally { setBusy(null); }
  };

  const toggle = async (m) => {
    const action = m.disabled ? "reactivate" : "deactivate";
    if (action === "deactivate" && !window.confirm(`Deactivate ${m.email}? They'll be locked out immediately.`)) return;
    setBusy(m.email);
    try {
      const { data } = await api.post(`/admin/team/${action}`, { email: m.email });
      toast.success(data.detail);
      reload();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="team-panel">
      <div className="text-xs tracking-widest text-volt flex items-center gap-2"><Users size={13} /> PLATFORM TEAM · ADMIN ACCESS</div>
      <p className="text-[11px] text-neutral-500 mt-1">Reset a teammate's password or lock them out instantly. Admin rights come from the server allowlist.</p>
      {!team ? (
        <div className="mt-4 text-neutral-500 text-sm">Loading…</div>
      ) : (
        <div className="mt-4 space-y-2" data-testid="team-list">
          {team.map((m) => (
            <div key={m.email} data-testid={`team-member-${m.email}`}
              className={`border p-3 flex flex-wrap items-center gap-3 ${m.disabled ? "border-red-900/60 bg-red-950/10" : "border-neutral-800 bg-appbg"}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white font-bold flex items-center gap-2 flex-wrap">
                  {m.email}
                  {m.is_self && <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-neutral-600 text-neutral-400">YOU</span>}
                  {m.disabled && <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-red-600 text-red-500" data-testid={`team-disabled-badge-${m.email}`}>DEACTIVATED</span>}
                  {!m.registered && <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-amber-600 text-amber-500">NOT REGISTERED</span>}
                </div>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  {m.name || "—"}{m.on_allowlist ? " · allowlist admin" : ""}{m.password_reset_at ? ` · pw reset ${m.password_reset_at.slice(0, 10)}` : ""}
                </div>
              </div>
              {m.registered && (
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => resetPw(m.email)} disabled={busy === m.email} data-testid={`team-reset-pw-${m.email}`}
                    className="min-h-[34px] px-3 border border-neutral-700 text-neutral-300 text-[10px] tracking-widest hover:border-volt hover:text-volt transition-colors disabled:opacity-50">
                    RESET PASSWORD
                  </button>
                  {!m.is_self && (
                    <button onClick={() => toggle(m)} disabled={busy === m.email} data-testid={`team-toggle-${m.email}`}
                      className={`min-h-[34px] px-3 border text-[10px] tracking-widest transition-colors disabled:opacity-50 ${m.disabled ? "border-volt text-volt hover:bg-volt hover:text-white" : "border-red-900/60 text-red-400 hover:border-red-500"}`}>
                      {m.disabled ? "REACTIVATE" : "DEACTIVATE"}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FoundingPartnersSection() {
  const [tab, setTab] = useState("comp");
  const [comped, setComped] = useState([]);
  const [codes, setCodes] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [generating, setGenerating] = useState(false);

  const reload = () => {
    api.get("/admin/comp-program/list").then(({ data }) => setComped(data)).catch(() => {});
    api.get("/admin/comp-codes").then(({ data }) => setCodes(data)).catch(() => {});
    api.get("/admin/feedback-queue").then(({ data }) => setDrafts(data)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  const generateDrafts = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/feedback-queue/generate");
      toast.success(`${data.drafts_created} new feedback drafts queued`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft generation failed");
    } finally { setGenerating(false); }
  };

  const downloadManual = async () => {
    try {
      const token = localStorage.getItem("cc_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/operating-manual.pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `coachconnect-ai-operating-manual-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Operating Manual downloaded");
    } catch (e) {
      toast.error("Couldn't download. Check you're signed in as a platform admin.");
    }
  };

  return (
    <div className="mt-8 border border-blaze/40 bg-gradient-to-br from-red-950/15 to-transparent p-5" data-testid="founding-partners-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 text-blaze text-xs tracking-widest"><Heart size={14} /> FOUNDING PARTNERS · COMP TOOLS</div>
        <button
          onClick={downloadManual}
          data-testid="download-ops-manual-btn"
          className="min-h-[36px] px-3 border border-blaze text-blaze text-[10px] tracking-widest hover:bg-blaze hover:text-white transition-colors flex items-center gap-1.5"
        >
          <FileText size={12} /> OPERATING MANUAL · PDF
        </button>
      </div>

      <div className="flex gap-1 border-b border-neutral-800 mb-5" data-testid="fp-tabs">
        {[
          { k: "comp", l: "Comp a Program", c: comped.length },
          { k: "codes", l: "Comp Codes", c: codes.length },
          { k: "feedback", l: "Feedback Queue", c: drafts.filter(d => d.status === "queued").length },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`fp-tab-${t.k}`}
            className={`px-4 py-2 text-xs tracking-widest border-b-2 transition-colors ${tab === t.k ? "border-blaze text-white" : "border-transparent text-neutral-400 hover:text-white"}`}
          >
            {t.l.toUpperCase()} <span className="ml-1 text-neutral-600">({t.c})</span>
          </button>
        ))}
      </div>

      {tab === "comp" && <CompProgramTab list={comped} onReload={reload} />}
      {tab === "codes" && <CompCodesTab list={codes} onReload={reload} />}
      {tab === "feedback" && <FeedbackQueueTab list={drafts} onReload={reload} onGenerate={generateDrafts} generating={generating} />}
    </div>
  );
}

function CompProgramTab({ list, onReload }) {
  const [form, setForm] = useState({
    program_name: "", ad_email: "", ad_name: "",
    tier: "hs_ad", duration_months: 6, reason: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.program_name || !form.ad_email) return toast.warning("Program name and AD email required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/comp-program", { ...form, duration_months: Number(form.duration_months) || 6 });
      toast.success(`${form.program_name} comped · invite ${data.email_sent ? "sent ✓" : "queued"}`);
      setForm({ program_name: "", ad_email: "", ad_name: "", tier: "hs_ad", duration_months: 6, reason: "" });
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Comp failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="border border-neutral-800 bg-appcard p-4 space-y-3" data-testid="comp-program-form">
        <p className="text-xs text-neutral-500">
          Comp a program directly — creates the AD account, seeds the program, marks subscription as
          complimentary, and emails them a magic-link to activate. <span className="text-volt">No Stripe touch.</span>
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FpField label="Program name" v={form.program_name} on={(v) => setForm({ ...form, program_name: v })} tid="comp-program-name" placeholder="e.g., Cape Henlopen HS" />
          <FpField label="AD email" v={form.ad_email} on={(v) => setForm({ ...form, ad_email: v })} tid="comp-ad-email" placeholder="ad@school.org" type="email" />
          <FpField label="AD name (optional)" v={form.ad_name} on={(v) => setForm({ ...form, ad_name: v })} tid="comp-ad-name" />
          <FpSelect label="Tier" v={form.tier} on={(v) => setForm({ ...form, tier: v })} tid="comp-tier" opts={[
            { v: "hs_ad", l: "HS Athletic Dept" },
            { v: "college_club", l: "College Club" },
            { v: "college_varsity", l: "College Varsity (D1/D2/D3)" },
          ]} />
          <FpField label="Duration (months)" v={form.duration_months} on={(v) => setForm({ ...form, duration_months: v })} tid="comp-duration" type="number" />
          <FpField label="Reason note" v={form.reason} on={(v) => setForm({ ...form, reason: v })} tid="comp-reason" placeholder="e.g., struggling local HS, founder-led pilot" />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="comp-submit-btn"
          className="min-h-[44px] px-5 bg-blaze text-white heading text-sm tracking-widest hover:glow-blaze transition-all disabled:opacity-50 flex items-center gap-2"
        >
          <Heart size={14} /> {busy ? "COMPING…" : "COMP THIS PROGRAM"}
        </button>
      </form>

      <div className="border border-neutral-800 bg-appcard p-4">
        <div className="text-xs tracking-widest text-volt mb-3">COMPED PROGRAMS · {list.length}</div>
        {list.length === 0 ? (
          <div className="text-xs text-neutral-500">No programs comped yet.</div>
        ) : (
          <div className="space-y-2">
            {list.map((c) => (
              <div key={c.id} className="border border-neutral-800 px-3 py-2 text-xs" data-testid={`comped-row-${c.id}`}>
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="heading text-sm text-white">{c.program_name || c.user_email}</span>
                  <span className="text-neutral-500">· {c.comp_tier}</span>
                  <span className="text-neutral-500">· {c.comp_duration_months}mo</span>
                  <span className="text-blaze ml-auto">{c.user_email}</span>
                </div>
                {c.comp_reason && <div className="text-neutral-500 mt-1">&ldquo;{c.comp_reason}&rdquo;</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CompCodesTab({ list, onReload }) {
  const [form, setForm] = useState({ code: "", discount_pct: 100, max_uses: 1, notes: "" });
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        code: form.code || undefined,
        discount_pct: Number(form.discount_pct),
        max_uses: Number(form.max_uses),
        notes: form.notes,
      };
      const { data } = await api.post("/admin/comp-codes", payload);
      toast.success(`Code ${data.code} created (${data.discount_pct}% off · ${data.max_uses} uses)`);
      setForm({ code: "", discount_pct: 100, max_uses: 1, notes: "" });
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code create failed");
    } finally { setBusy(false); }
  };

  const deactivate = async (id) => {
    try {
      await api.delete(`/admin/comp-codes/${id}`);
      toast.success("Code deactivated");
      onReload();
    } catch (e) { toast.error("Deactivate failed"); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={create} className="border border-neutral-800 bg-appcard p-4 space-y-3" data-testid="comp-code-form">
        <p className="text-xs text-neutral-500">
          Reusable codes for ADs you can&apos;t comp directly. They paste at checkout; we apply the discount via Stripe.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <FpField label="Code (blank = auto)" v={form.code} on={(v) => setForm({ ...form, code: v.toUpperCase() })} tid="code-input" placeholder="e.g., CAPE-HS-2026" />
          <FpField label="Discount %" v={form.discount_pct} on={(v) => setForm({ ...form, discount_pct: v })} tid="code-discount" type="number" />
          <FpField label="Max uses" v={form.max_uses} on={(v) => setForm({ ...form, max_uses: v })} tid="code-max-uses" type="number" />
          <FpField label="Notes" v={form.notes} on={(v) => setForm({ ...form, notes: v })} tid="code-notes" />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="code-create-btn"
          className="min-h-[44px] px-5 bg-blaze text-white heading text-sm tracking-widest hover:glow-blaze transition-all disabled:opacity-50 flex items-center gap-2"
        >
          <Plus size={14} /> {busy ? "CREATING…" : "CREATE CODE"}
        </button>
      </form>

      <div className="border border-neutral-800 bg-appcard p-4">
        <div className="text-xs tracking-widest text-volt mb-3">CODES · {list.length}</div>
        {list.length === 0 ? (
          <div className="text-xs text-neutral-500">No codes created yet.</div>
        ) : (
          <div className="space-y-2">
            {list.map((c) => (
              <div key={c.id} className="border border-neutral-800 px-3 py-2 text-xs flex items-baseline gap-2 flex-wrap" data-testid={`code-row-${c.id}`}>
                <span className="heading text-sm text-white font-mono">{c.code}</span>
                <span className="text-blaze">{c.discount_pct}% OFF</span>
                <span className="text-neutral-500">· uses: {c.uses}/{c.max_uses}</span>
                {!c.active && <span className="text-red-400">· DEACTIVATED</span>}
                {c.notes && <span className="text-neutral-500">· {c.notes}</span>}
                {c.active && (
                  <button onClick={() => deactivate(c.id)} data-testid={`code-deactivate-${c.id}`} className="ml-auto text-[10px] tracking-widest border border-neutral-700 px-2 py-1 hover:border-blaze hover:text-blaze">DEACTIVATE</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FeedbackQueueTab({ list, onReload, onGenerate, generating }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-neutral-500 max-w-xl">
          Auto-drafts 30 / 90 / 180-day check-ins for every comped program. Claude
          writes them; you review and click Send via Resend.
        </p>
        <button
          onClick={onGenerate}
          disabled={generating}
          data-testid="generate-drafts-btn"
          className="min-h-[40px] px-4 border border-volt text-volt text-xs tracking-widest hover:bg-volt hover:text-white transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <Sparkles size={14} /> {generating ? "GENERATING…" : "GENERATE MILESTONE DRAFTS"}
        </button>
      </div>

      <div className="space-y-2">
        {list.length === 0 && (
          <div className="text-xs text-neutral-500 border border-neutral-800 px-3 py-3 bg-appcard">
            No feedback drafts yet. Generate after at least one program has been comped for 30+ days.
          </div>
        )}
        {list.map((d) => (
          <FeedbackDraftCard key={d.id} draft={d} onReload={onReload} />
        ))}
      </div>
    </div>
  );
}

function FeedbackDraftCard({ draft, onReload }) {
  const [text, setText] = useState(draft.draft);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/feedback-queue/${draft.id}/edit`, { draft: text });
      toast.success("Draft saved");
      setEditing(false);
      onReload();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/feedback-queue/${draft.id}/send`);
      toast.success(data.sent ? "Sent via Resend ✓" : "Send failed");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  const sent = draft.status === "sent";

  return (
    <div className="border border-neutral-800 bg-appcard p-3" data-testid={`feedback-draft-${draft.id}`}>
      <div className="flex items-baseline gap-2 flex-wrap text-xs">
        <span className="heading text-sm text-white">{draft.program_name}</span>
        <span className={`text-[10px] tracking-widest px-2 py-0.5 border ${draft.milestone === "30d" ? "border-volt text-volt" : draft.milestone === "90d" ? "border-blaze text-blaze" : "border-white text-white"}`}>
          {draft.milestone.toUpperCase()}
        </span>
        <span className="text-neutral-500">→ {draft.ad_email}</span>
        {sent && <span className="ml-auto text-volt text-[10px]">✓ SENT</span>}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={!editing || sent}
        data-testid={`feedback-text-${draft.id}`}
        rows={6}
        className="mt-3 w-full bg-[#070707] border border-neutral-800 px-2 py-2 text-[11px] text-neutral-300 font-mono whitespace-pre-wrap disabled:opacity-80"
      />
      {!sent && (
        <div className="flex items-center gap-2 mt-2">
          {editing ? (
            <button onClick={save} disabled={busy} data-testid={`feedback-save-${draft.id}`} className="min-h-[36px] px-3 border border-volt text-volt text-[10px] tracking-widest hover:bg-volt hover:text-white disabled:opacity-50">
              {busy ? "SAVING…" : "SAVE EDIT"}
            </button>
          ) : (
            <button onClick={() => setEditing(true)} data-testid={`feedback-edit-${draft.id}`} className="min-h-[36px] px-3 border border-neutral-700 text-[10px] tracking-widest hover:border-volt hover:text-volt">EDIT</button>
          )}
          <button onClick={send} disabled={busy} data-testid={`feedback-send-${draft.id}`} className="ml-auto min-h-[36px] px-3 bg-blaze text-white text-[10px] tracking-widest hover:glow-blaze transition-all disabled:opacity-50 flex items-center gap-1.5">
            <Mail size={12} /> {busy ? "SENDING…" : "SEND VIA RESEND"}
          </button>
        </div>
      )}
    </div>
  );
}

function FpField({ label, v, on, tid, type = "text", placeholder = "" }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-widest text-neutral-500 mb-1">{label.toUpperCase()}</div>
      <input
        type={type}
        value={v}
        onChange={(e) => on(e.target.value)}
        data-testid={tid}
        placeholder={placeholder}
        className="w-full bg-[#070707] border border-neutral-800 px-3 py-2 text-xs text-neutral-200 outline-none focus:border-blaze"
      />
    </label>
  );
}

function FpSelect({ label, v, on, tid, opts }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-widest text-neutral-500 mb-1">{label.toUpperCase()}</div>
      <select
        value={v}
        onChange={(e) => on(e.target.value)}
        data-testid={tid}
        className="w-full bg-[#070707] border border-neutral-800 px-3 py-2 text-xs text-neutral-200 outline-none focus:border-blaze"
      >
        {opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
